import React, { useState } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, Stepper, Step, StepLabel, useMediaQuery, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { en } from '../translations/en';
import { logger } from '../utils/logger';
import { usePasswordVisibility } from '../hooks/usePasswordVisibility';
import { validatePassword } from '../utils/passwordValidation';

export const ForgotPassword: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { forgotPassword, confirmPassword, error, isLoading } = useAuth();
  const navigate = useNavigate();
  const { showPassword: showNewPassword, inputType: newPasswordInputType, toggleVisibility: toggleNewPasswordVisibility } = usePasswordVisibility();
  const { showPassword: showConfirmPassword, inputType: confirmPasswordInputType, toggleVisibility: toggleConfirmPasswordVisibility } = usePasswordVisibility();

  const steps = [en.forgotPassword.steps.enterUsername, en.forgotPassword.steps.enterCodeAndPassword];

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    try {
      await forgotPassword(username);
      setActiveStep(1);
    } catch (error) {
      // Error handled by context
      logger.debug('Forgot password request failed', error);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (newPassword !== confirmNewPassword) {
      setLocalError(en.forgotPassword.errors.passwordMismatch);
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setLocalError(validation.errors.join('. '));
      return;
    }

    try {
      await confirmPassword(username, code, newPassword);
      navigate('/auth');
    } catch (error) {
      // Error handled by context
      logger.debug('Password confirmation failed', error);
    }
  };

  const currentError = localError || error;

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      p: 2
    }}>
      <Card sx={{
        maxWidth: 500,
        width: '100%',
        boxShadow: theme.shadows[4]
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              color: theme.palette.primary.main,
              fontFamily: 'Libre Baskerville, serif'
            }}
          >
            {en.forgotPassword.title}
          </Typography>

          <Stepper activeStep={activeStep} alternativeLabel={isMobile} sx={{ mb: { xs: 2, sm: 4 } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box component="form" onSubmit={handleSendCode}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {en.forgotPassword.enterUsernameDescription}
              </Typography>
              <TextField
                fullWidth
                label={en.forgotPassword.username}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                margin="normal"
                required
              />
              {currentError && <Alert severity="error" sx={{ mt: 2 }}>{currentError}</Alert>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark
                  }
                }}
              >
                {isLoading ? en.forgotPassword.sendingCode : en.forgotPassword.sendResetCode}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box component="form" onSubmit={handleResetPassword}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {en.forgotPassword.enterCodeDescription}
              </Typography>
              <TextField
                fullWidth
                label={en.forgotPassword.verificationCode}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={en.forgotPassword.newPassword}
                type={newPasswordInputType}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label="toggle new password visibility"
                        onClick={toggleNewPasswordVisibility}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label={en.forgotPassword.confirmNewPassword}
                type={confirmPasswordInputType}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label="toggle confirm password visibility"
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {currentError && <Alert severity="error" sx={{ mt: 2 }}>{currentError}</Alert>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark
                  }
                }}
              >
                {isLoading ? en.forgotPassword.resettingPassword : en.forgotPassword.resetPassword}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => setActiveStep(0)}
                sx={{ mt: 1 }}
              >
                {en.forgotPassword.backToUsername}
              </Button>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link to="/auth" style={{ textDecoration: 'none' }}>
              <Button variant="text">
                {en.forgotPassword.backToSignIn}
              </Button>
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
